"use client";

import { useEffect } from "react";

import {
  SUPPLY_NAV_COOKIE,
  SUPPLY_NAV_COOKIE_MAX_AGE,
  encodeNavCookie,
  type SupplyNavKey,
} from "../_lib/nav";

/**
 * Persists which supply surfaces are flag-live into `fb_supply_nav`, so the
 * NEXT document load's boot script can hide dead rail entries PRE-PAINT — the
 * same trick fb_supply_tier plays for the Approvals tab, and for the same
 * reason: a route-level loading.tsx cannot read system flags (it renders before
 * the page's server work), so if the rail filtered on a server prop the entry
 * list would reflow on every navigation.
 *
 * Rendered by the rail only when a page passed `surfaces`; loading skeletons
 * omit it and reuse whatever the cookie already holds. Also writes the live
 * `data-supply-nav` attribute so the CURRENT page corrects itself on first
 * ever visit (empty cookie) instead of waiting for the next navigation.
 *
 * Writing a cookie from a Server Component would force the layout dynamic and
 * break PPR, and is not allowed during render — hence a mount effect.
 */
export function SupplyNavCookieSync({ surfaces }: { surfaces: SupplyNavKey[] }) {
  const value = encodeNavCookie(surfaces);
  useEffect(() => {
    document.cookie = `${SUPPLY_NAV_COOKIE}=${value}; path=/; max-age=${SUPPLY_NAV_COOKIE_MAX_AGE}; SameSite=Lax`;
    const root = document.querySelector<HTMLElement>("[data-supply]");
    if (root) root.dataset.supplyNav = decodeURIComponent(value);
  }, [value]);

  return <span data-supply-nav-sync hidden />;
}
